import React, { useState } from 'react';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Award,
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  Eye,
  MoreVertical,
  Download,
  Mail
} from 'lucide-react';

const LMS = () => {
  const [activeTab, setActiveTab] = useState('courses');
  const [searchTerm, setSearchTerm] = useState('');

  const [courses, setCourses] = useState([
    { id: 1, title: 'Land Management Basics', duration: '4 weeks', instructor: 'Dr. Kumar', status: 'active', enrolled: 45, progress: 75, rating: 4.5 },
    { id: 2, title: 'Survey Techniques', duration: '6 weeks', instructor: 'Prof. Singh', status: 'active', enrolled: 32, progress: 60, rating: 4.8 },
    { id: 3, title: 'Legal Framework', duration: '8 weeks', instructor: 'Adv. Patel', status: 'draft', enrolled: 0, progress: 0, rating: 0 },
    { id: 4, title: 'GIS & Mapping', duration: '5 weeks', instructor: 'Dr. Reddy', status: 'active', enrolled: 28, progress: 85, rating: 4.6 },
  ]);

  const [students, setStudents] = useState([
    { id: 1, name: 'Rajesh Kumar', email: 'rajesh@example.com', course: 'Land Management Basics', progress: 75, status: 'active', enrolled: '2024-01-15' },
    { id: 2, name: 'Priya Singh', email: 'priya@example.com', course: 'Survey Techniques', progress: 60, status: 'active', enrolled: '2024-02-01' },
    { id: 3, name: 'Amit Patel', email: 'amit@example.com', course: 'Land Management Basics', progress: 90, status: 'active', enrolled: '2024-01-20' },
    { id: 4, name: 'Sneha Reddy', email: 'sneha@example.com', course: 'GIS & Mapping', progress: 45, status: 'active', enrolled: '2024-02-10' },
  ]);

  const stats = {
    totalCourses: courses.length,
    activeCourses: courses.filter(c => c.status === 'active').length,
    totalStudents: students.length,
    avgCompletion: Math.round(students.reduce((sum, s) => sum + s.progress, 0) / students.length)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Learning Management System</h1>
          <p className="text-gray-600 mt-1">Manage courses, students, and learning progress</p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors shadow-sm">
          <Plus className="w-5 h-5" />
          {activeTab === 'courses' ? 'Add Course' : 'Add Student'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Courses</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalCourses}</p>
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                <span>{stats.activeCourses} active</span>
              </p>
            </div>
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-[#3d82c4]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Students</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalStudents}</p>
              <p className="text-xs text-gray-500 mt-2">Enrolled learners</p>
            </div>
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Avg Completion</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.avgCompletion}%</p>
              <p className="text-xs text-gray-500 mt-2">Overall progress</p>
            </div>
            <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center">
              <Award className="w-7 h-7 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Enrollments</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{courses.reduce((sum, c) => sum + c.enrolled, 0)}</p>
              <p className="text-xs text-gray-500 mt-2">Course enrollments</p>
            </div>
            <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex gap-6">
            {['courses', 'students', 'analytics'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  pb-3 px-1 font-medium text-sm border-b-2 transition-colors capitalize
                  ${activeTab === tab
                    ? 'border-[#3d82c4] text-[#3d82c4]'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
              <Filter className="w-5 h-5" />
              Filter
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
              <Download className="w-5 h-5" />
              Export
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'courses' && (
            <div className="space-y-4">
              {courses.filter(c => c.title.toLowerCase().includes(searchTerm.toLowerCase())).map(course => (
                <div key={course.id} className="bg-gray-50 rounded-lg p-5 hover:bg-gray-100 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      <div className="w-12 h-12 bg-[#3d82c4] rounded-lg flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-800">{course.title}</h3>
                          <span className={`
                            px-2.5 py-1 rounded-full text-xs font-medium
                            ${course.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}
                          `}>
                            {course.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                          <span>👨‍🏫 {course.instructor}</span>
                          <span>⏱️ {course.duration}</span>
                          <span>👥 {course.enrolled} students</span>
                          {course.rating > 0 && <span>⭐ {course.rating}</span>}
                        </div>
                        {course.progress > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600">Course Progress</span>
                              <span className="font-semibold text-gray-800">{course.progress}%</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] rounded-full transition-all"
                                style={{ width: `${course.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-gray-600 hover:bg-white rounded-lg transition-colors">
                        <Eye className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-600 hover:bg-white rounded-lg transition-colors">
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-white rounded-lg transition-colors">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'students' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Student</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Course</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Progress</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Enrolled</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase())).map(student => (
                    <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-800">{student.name}</p>
                          <p className="text-sm text-gray-500">{student.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">{student.course}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[100px]">
                            <div 
                              className="h-full bg-[#3d82c4] rounded-full"
                              style={{ width: `${student.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{student.progress}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{student.enrolled}</td>
                      <td className="py-4 px-4">
                        <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          {student.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <Mail className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Course Performance</h3>
                  <div className="space-y-3">
                    {courses.filter(c => c.status === 'active').map(course => (
                      <div key={course.id}>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-700 font-medium">{course.title}</span>
                          <span className="text-gray-600">{course.progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad]"
                            style={{ width: `${course.progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Performers</h3>
                  <div className="space-y-3">
                    {students.sort((a, b) => b.progress - a.progress).slice(0, 5).map((student, idx) => (
                      <div key={student.id} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#3d82c4] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{student.name}</p>
                          <p className="text-xs text-gray-500">{student.course}</p>
                        </div>
                        <span className="text-lg font-bold text-[#3d82c4]">{student.progress}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LMS;